import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PortalNav } from "@/components/PortalNav";
import { CustomerMap } from "@/components/CustomerMap";
import { RepPicker } from "@/components/RepPicker";
import { QuickAddProspect } from "@/components/QuickAddProspect";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MapPin, Loader2, ChevronLeft, ChevronRight, Navigation, CheckCircle2, Circle, Clock, Phone } from "lucide-react";
import { toast } from "sonner";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shift = (s, n) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const mapsUrl = (s) => s.lat != null && s.lng != null
  ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`
  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || s.company_name)}`;

export default function CrmMyDay() {
  const { user } = useAuth();
  const [date, setDate] = useState(iso(new Date()));
  const [repId, setRepId] = useState(user?.id || "all");
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get("/crm/daily-stops", { params: { date, rep_id: repId } })
      .then(({ data }) => setStops(data.stops || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load stops"))
      .finally(() => setLoading(false));
  }, [date, repId, reloadKey]);

  const toggle = async (s) => {
    const done = !s.done;
    setStops((xs) => xs.map((x) => (x.call_id === s.call_id && x.kind === s.kind ? { ...x, done } : x)));
    try { await api.post("/crm/daily-stops/done", { call_id: s.call_id, kind: s.kind, done }); }
    catch (e) { setStops((xs) => xs.map((x) => (x.call_id === s.call_id && x.kind === s.kind ? { ...x, done: !done } : x))); toast.error("Could not update stop"); }
  };

  const points = useMemo(() => stops.map((s) => ({ lat: s.lat, lng: s.lng, number: s.order, done: s.done, label: `<b>${s.order}. ${s.company_name}</b><br/>${s.address || ""}` })), [stops]);
  const doneCount = stops.filter((s) => s.done).length;

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10" data-testid="my-day-page">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">Daily Stops</p>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#0F172A] flex items-center gap-3">
            <MapPin className="h-8 w-8 text-[#D4AF37]" /> My Day
          </h1>
          <p className="mt-2 text-sm text-zinc-500">Visits and follow-ups scheduled for the selected day.</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="flex items-center border border-zinc-300 bg-white">
              <button onClick={() => setDate(shift(date, -1))} data-testid="my-day-prev" className="px-2 py-2 hover:bg-zinc-100"><ChevronLeft className="h-4 w-4" /></button>
              <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} data-testid="my-day-date" className="px-2 py-1.5 text-sm outline-none" />
              <button onClick={() => setDate(shift(date, 1))} data-testid="my-day-next" className="px-2 py-2 hover:bg-zinc-100"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <button onClick={() => setDate(iso(new Date()))} data-testid="my-day-today" className="px-4 py-2 text-sm border border-zinc-300 bg-white hover:bg-zinc-100">Today</button>
            <RepPicker value={repId} onChange={setRepId} testid="my-day-rep-picker" />
            <span className="ml-auto text-sm text-zinc-500" data-testid="my-day-progress">{doneCount} / {stops.length} stops done</span>
          </div>
          <QuickAddProspect date={date} repId={repId} onAdded={() => setReloadKey((k) => k + 1)} />
          {loading ? (
            <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>
          ) : (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2 space-y-3" data-testid="my-day-stops">
                {stops.length === 0 && <div className="bg-white border border-zinc-200 p-12 text-center text-sm text-zinc-400" data-testid="my-day-empty">No stops for this day.</div>}
                {stops.map((s) => <StopCard key={`${s.call_id}-${s.kind}`} s={s} onToggle={() => toggle(s)} showRep={repId === "all"} />)}
              </div>
              <div className="lg:col-span-3 lg:sticky lg:top-6 self-start">
                <CustomerMap points={points} height={480} testid="my-day-map" />
                {stops.some((s) => s.lat == null) && <p className="text-xs text-zinc-400 mt-2">Some stops have no GPS location and are not shown on the map.</p>}
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}

export function StopCard({ s, onToggle, showRep, profileBase = "/crm/customers" }) {
  const navigate = useNavigate();
  const tid = `stop-${s.call_id}-${s.kind}`;
  return (
    <div data-testid={tid} className={`bg-white border p-4 flex gap-3 transition-colors ${s.done ? "border-[#10B981]/40 bg-[#10B981]/5" : "border-zinc-200"}`}>
      <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 border-2 border-[#D4AF37] ${s.done ? "bg-[#10B981] text-white" : "bg-[#0F172A] text-white"}`}>{s.order}</div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => navigate(`${profileBase}/${s.customer_id}`)} data-testid={`${tid}-open`} className={`font-medium text-[#0F172A] hover:underline text-left ${s.done ? "line-through text-zinc-400" : ""}`}>{s.company_name || "Customer"}</button>
          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border ${s.kind === "followup" ? "border-[#0F172A] text-[#0F172A]" : "border-[#D4AF37] text-[#B8860B]"}`}>{s.label}</span>
          {showRep && <span className="text-[10px] text-zinc-400">{s.rep_name}</span>}
        </div>
        {s.address && <p className="text-xs text-zinc-500 mt-1">{s.address}</p>}
        <div className="flex flex-wrap gap-x-3 text-xs text-zinc-500 mt-1">
          {s.time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{s.time}</span>}
          {s.contact && <span>{s.contact}</span>}
          {s.phone && <a href={`tel:${s.phone}`} className="flex items-center gap-1 hover:text-[#0F172A]"><Phone className="h-3 w-3" />{s.phone}</a>}
        </div>
        {s.notes && <p className="text-sm text-zinc-600 mt-1">{s.notes}</p>}
        <div className="flex gap-2 mt-3">
          <button onClick={onToggle} data-testid={`${tid}-done`} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border transition-colors ${s.done ? "border-[#10B981] text-[#10B981]" : "border-zinc-300 text-zinc-600 hover:border-[#0F172A]"}`}>
            {s.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}{s.done ? "Done" : "Mark done"}
          </button>
          <a href={mapsUrl(s)} target="_blank" rel="noreferrer" data-testid={`${tid}-maps`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#0F172A] text-white hover:bg-[#1E293B]">
            <Navigation className="h-3.5 w-3.5" /> Google Maps
          </a>
        </div>
      </div>
    </div>
  );
}
